import React from "react";
import { LoadingOutlined, CheckCircleFilled } from "@ant-design/icons";
import { usePrintReadinessPending } from "./print-readiness";

type Props = {
  mountVisible: number;
  mountTotal: number;
  renderComplete: boolean;
};

/**
 * Sticky bottom status bar — always visible while the print view is still
 * preparing; hides itself once both chunked mounting AND rasterization are
 * complete. Gives the user an ever-present answer to "is this stuck?".
 *
 * Two independent progress tracks:
 *   1. Mount  — how many rows have React-mounted (chunked by CHUNK_SIZE)
 *   2. Raster — how many per-row Gantt rasterize slots are still pending
 *
 * Both read from live sources (props + readiness context), so the bar
 * updates continuously without polling.
 *
 * Hidden in print via `print:hidden` so it never leaks onto paper.
 */
function RenderProgressBar({
  mountVisible,
  mountTotal,
  renderComplete
}: Props) {
  const pendingRaster = usePrintReadinessPending();

  const mountPct =
    mountTotal > 0 ? Math.round((mountVisible / mountTotal) * 100) : 100;
  const mountDone = mountVisible >= mountTotal;
  const allDone = mountDone && renderComplete && pendingRaster === 0;

  if (allDone || mountTotal === 0) return null;

  return (
    <div
      className="fixed bottom-0 inset-x-0 z-50 print:hidden"
      role="status"
      aria-live="polite"
    >
      <div
        className="mx-auto max-w-5xl m-3 rounded-lg shadow-lg border border-gray-200 bg-white/95 backdrop-blur"
        style={{ boxShadow: "0 8px 24px rgba(0,0,0,0.12)" }}
      >
        {/* Top line — progress bar + status label */}
        <div className="flex items-center gap-3 px-4 py-2.5">
          <LoadingOutlined style={{ fontSize: 16, color: "#4448C1" }} spin />
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-3 text-[12px] font-medium text-gray-900">
              <span>
                {mountDone
                  ? "Finalising diagrams"
                  : `Rendering rows · ${mountVisible} of ${mountTotal}`}
              </span>
              <span className="text-gray-500 font-normal tabular-nums">
                {mountDone
                  ? pendingRaster === 0
                    ? "Almost ready…"
                    : `${pendingRaster} pending`
                  : `${mountPct}%`}
              </span>
            </div>
            {/* Progress track */}
            <div className="mt-1 h-[3px] w-full bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-[#4448C1] transition-[width] duration-200 ease-out"
                style={{
                  width: mountDone ? "100%" : `${mountPct}%`
                }}
              />
            </div>
          </div>
        </div>

        {/* Bottom sub-line — two-track detail so users understand there's
            a second phase after mount. Collapses once nothing's pending. */}
        {(mountDone || pendingRaster > 0) && (
          <div className="px-4 pb-2 -mt-0.5 flex items-center gap-4 text-[12px] text-gray-500">
            <span className="inline-flex items-center gap-1.5">
              {mountDone ? (
                <CheckCircleFilled style={{ color: "#16a34a", fontSize: 12 }} />
              ) : (
                <span className="inline-block w-2 h-2 rounded-full bg-gray-300" />
              )}
              Rows mounted {mountVisible}/{mountTotal}
            </span>
            <span className="inline-flex items-center gap-1.5">
              {pendingRaster === 0 && mountDone ? (
                <CheckCircleFilled style={{ color: "#16a34a", fontSize: 12 }} />
              ) : (
                <span className="inline-block w-2 h-2 rounded-full bg-amber-400" />
              )}
              Diagrams pending {pendingRaster}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default React.memo(RenderProgressBar);
